/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import { useState, useEffect } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'

export const useRole = () => {
  const [role, setRole] = useState('attendee')
  const [speakerSlugs, setSpeakerSlugs] = useState([])
  const [loading, setLoading] = useState(true)

  const loadRole = async () => {
    try {
      const session = await fetchAuthSession()
      const groups = session.tokens?.accessToken?.payload['cognito:groups'] || []
      const attrs = session.tokens?.idToken?.payload

      // Priority: admin > host > speaker > attendee
      if (groups.includes('admin')) setRole('admin')
      else if (groups.includes('host')) setRole('host')
      else if (groups.includes('speaker')) setRole('speaker')
      else setRole('attendee')

      // Parse speaker slugs from custom attribute
      const slugs = attrs?.['custom:speakerSlug']
      setSpeakerSlugs(slugs ? slugs.split(',').filter(Boolean) : [])
    } catch (error) {
      console.error('Failed to load role:', error)
      setRole('attendee')
      setSpeakerSlugs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRole()

    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'signedOut' || payload.event === 'signInWithRedirect') {
        loadRole()
      }
    })

    return () => { hubListener() }
  }, [])

  const canAnswerSession = (sessionSpeakers) => {
    if (role === 'admin' || role === 'host') return true
    if (role === 'speaker' && speakerSlugs.length) {
      // sessionSpeakers = array of speaker objects with _path
      if (Array.isArray(sessionSpeakers)) {
        return sessionSpeakers.some((s) => {
          const slug = s._path?.split('/').filter(Boolean).slice(-3).join('/')
          return speakerSlugs.includes(slug)
        })
      }
      // Fallback: sessionSpeakers is a string (session path/id)
      return speakerSlugs.some((slug) => sessionSpeakers?.includes(slug))
    }
    return false
  }

  return {
    role,
    speakerSlugs,
    canAnswerSession,
    loading,
  }
}
